import { useEffect, useRef } from "react";

export interface UseBarcodeScannerOptions {
  onScan: (code: string) => void;
  scanTimeout?: number; // ms between characters
  minLength?: number;
  enabled?: boolean;
  preventInInput?: boolean;
}

export function useBarcodeScanner({
  onScan,
  scanTimeout = 50,
  minLength = 5,
  enabled = true,
  preventInInput = true,
}: UseBarcodeScannerOptions): void {
  const bufferRef = useRef<string>("");
  const lastTimeRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (preventInInput) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
          return;
        }
      }

      const now = Date.now();

      if (now - lastTimeRef.current > scanTimeout) {
        bufferRef.current = "";
      }

      lastTimeRef.current = now;

      if (e.key === "Enter") {
        if (bufferRef.current.length >= minLength) {
          e.preventDefault();
          e.stopPropagation();
          onScan(bufferRef.current);
        }
        bufferRef.current = "";
        return;
      }

      if (e.key.length === 1) {
        bufferRef.current += e.key;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onScan, scanTimeout, minLength, enabled, preventInInput]);
}
